import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import type { UploadKind } from '@zoxo/shared'
import { EditorContent, useEditor, type Editor, type JSONContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Placeholder from '@tiptap/extension-placeholder'
import Highlight from '@tiptap/extension-highlight'
import { Table } from '@tiptap/extension-table'
import { TableRow } from '@tiptap/extension-table-row'
import { TableHeader } from '@tiptap/extension-table-header'
import { TableCell } from '@tiptap/extension-table-cell'
import { motion } from 'framer-motion'
import {
  ArrowDownToLine,
  ArrowLeftToLine,
  ArrowRightToLine,
  ArrowUpToLine,
  Bold,
  Code,
  Columns3,
  Heading1,
  Heading2,
  Heading3,
  Highlighter,
  Image as ImageIcon,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Quote,
  Rows3,
  Strikethrough,
  Table as TableIcon,
  Trash2,
  Underline as UnderlineIcon,
} from '@/components/icons'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { UploadValidationError, useImageUpload } from './use-upload'
import { ImageWithCaption } from './image-with-caption'
import { ArticlePaper } from '../article-paper'

/**
 * Rich-text article editor — toolbar composition matched 1:1 to Seasonly's
 * magazine editor (H1/H2/H3, bold/italic/underline/strike/highlight, lists,
 * quote/code, link/image, table). StarterKit v3 already bundles link + underline
 * + lists, so they are configured there (no separate extensions → no "duplicate
 * extension" warnings). Body is a tiptap JSON document (zoxo contract); image
 * upload reuses the article-namespaced R2 flow. (No poll button — polls are a
 * separate pillar.)
 */
const DEFAULT_PLACEHOLDER = '본문 평어체로 작성'

const BASE_EXTENSIONS = [
  StarterKit.configure({
    heading: { levels: [1, 2, 3] },
    link: {
      openOnClick: false,
      autolink: true,
      HTMLAttributes: { class: 'text-primary underline' },
    },
  }),
  ImageWithCaption.configure({ HTMLAttributes: { class: 'rounded-lg' } }),
  Highlight,
  Table.configure({ resizable: true, HTMLAttributes: { class: 'tiptap-table' } }),
  TableRow,
  TableHeader,
  TableCell,
]

function ToolbarButton({
  onClick,
  isActive,
  disabled,
  title,
  children,
}: {
  onClick: () => void
  isActive?: boolean
  disabled?: boolean
  title: string
  children: ReactNode
}) {
  return (
    <motion.button
      type="button"
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={title}
      aria-pressed={isActive}
      className={cn(
        'rounded-md p-1.5 transition-colors',
        isActive
          ? 'bg-primary/10 text-primary'
          : 'text-text-muted hover:bg-surface-muted',
        disabled && 'cursor-not-allowed opacity-50',
      )}
    >
      {children}
    </motion.button>
  )
}

function Divider() {
  return <div className="mx-0.5 h-5 w-px bg-border" />
}

function Toolbar({
  editor,
  onImageClick,
  onLinkClick,
  uploading,
  extra,
}: {
  editor: Editor
  onImageClick: () => void
  onLinkClick: () => void
  uploading: boolean
  /** 맨 끝에 붙는 도구(커뮤니티의 「설문 추가」 같은 것). 편집기 밖의 일이라 호출자가 그린다. */
  extra?: ReactNode
}) {
  const s = 'h-4 w-4'
  return (
    <div className="sticky top-12 z-30 mb-4 sm:top-[89px] flex flex-wrap items-center gap-0.5 rounded-lg border border-border bg-surface px-1 py-0.5">
      <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} isActive={editor.isActive('heading', { level: 1 })} title="제목 1">
        <Heading1 className={s} />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} isActive={editor.isActive('heading', { level: 2 })} title="제목 2">
        <Heading2 className={s} />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} isActive={editor.isActive('heading', { level: 3 })} title="제목 3">
        <Heading3 className={s} />
      </ToolbarButton>

      <Divider />

      <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} isActive={editor.isActive('bold')} title="굵게">
        <Bold className={s} />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} isActive={editor.isActive('italic')} title="기울임">
        <Italic className={s} />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleUnderline().run()} isActive={editor.isActive('underline')} title="밑줄">
        <UnderlineIcon className={s} />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleStrike().run()} isActive={editor.isActive('strike')} title="취소선">
        <Strikethrough className={s} />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleHighlight().run()} isActive={editor.isActive('highlight')} title="형광펜">
        <Highlighter className={s} />
      </ToolbarButton>

      <Divider />

      <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} isActive={editor.isActive('bulletList')} title="목록">
        <List className={s} />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} isActive={editor.isActive('orderedList')} title="번호 목록">
        <ListOrdered className={s} />
      </ToolbarButton>

      <Divider />

      <ToolbarButton onClick={() => editor.chain().focus().toggleBlockquote().run()} isActive={editor.isActive('blockquote')} title="인용구">
        <Quote className={s} />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleCodeBlock().run()} isActive={editor.isActive('codeBlock')} title="코드 블록">
        <Code className={s} />
      </ToolbarButton>

      <Divider />

      <ToolbarButton onClick={onLinkClick} isActive={editor.isActive('link')} title="링크 추가">
        <LinkIcon className={s} />
      </ToolbarButton>
      <ToolbarButton onClick={onImageClick} disabled={uploading} title="이미지 추가">
        <ImageIcon className={s} />
      </ToolbarButton>

      <Divider />

      <ToolbarButton onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} title="표 삽입 (3×3)">
        <TableIcon className={s} />
      </ToolbarButton>
      {editor.isActive('table') && (
        <>
          <ToolbarButton onClick={() => editor.chain().focus().addRowBefore().run()} title="위에 행 추가">
            <ArrowUpToLine className={s} />
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().addRowAfter().run()} title="아래에 행 추가">
            <ArrowDownToLine className={s} />
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().deleteRow().run()} title="행 삭제">
            <Rows3 className={s} />
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().addColumnBefore().run()} title="왼쪽에 열 추가">
            <ArrowLeftToLine className={s} />
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().addColumnAfter().run()} title="오른쪽에 열 추가">
            <ArrowRightToLine className={s} />
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().deleteColumn().run()} title="열 삭제">
            <Columns3 className={s} />
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().deleteTable().run()} title="표 삭제">
            <Trash2 className={s} />
          </ToolbarButton>
        </>
      )}
      {extra ? (
        <>
          <Divider />
          {extra}
        </>
      ) : null}
    </div>
  )
}

/** 툴바 버튼을 편집기 밖(호출자)에서도 같은 모양으로 쓴다 — 커뮤니티의 「설문 추가」. */
export { ToolbarButton }

export function RichEditor({
  value,
  onChange,
  ariaLabel = '기사 본문',
  uploadKind = 'article',
  placeholder = DEFAULT_PLACEHOLDER,
  toolbarExtra,
  paperClassName,
  afterBody,
}: {
  value: JSONContent | undefined
  onChange: (doc: JSONContent) => void
  ariaLabel?: string
  /** Namespaces uploaded images by feature (e.g. 'survey' → surveys/<YYYYMM>/...). */
  uploadKind?: UploadKind
  placeholder?: string
  /** 툴바 맨 끝에 붙는 도구. */
  toolbarExtra?: ReactNode
  /**
   * 종이 바깥 상자에 더할 클래스. 편집기 카드의 안쪽 여백을 -mx 로 상쇄해 종이 폭을
   * 읽기 화면과 같게 만든다 — 폭이 다르면 줄바꿈이 달라져 배치가 어긋난다.
   */
  paperClassName?: string
  /** 종이 안, 본문 아래에 함께 놓는 것(커뮤니티의 설문 만들기). 읽기 화면의 설문 자리와 같다. */
  afterBody?: ReactNode
}) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const upload = useImageUpload(uploadKind)
  const [linkOpen, setLinkOpen] = useState(false)
  const [linkValue, setLinkValue] = useState('')

  const extensions = useMemo(
    () => [...BASE_EXTENSIONS, Placeholder.configure({ placeholder })],
    [placeholder],
  )

  /*
    본문 상자의 클래스. 상세의 종이(.article-paper)와 같은 명조체 — 같은 글꼴이어야 줄 길이가 맞는다.
    **본문 아래에 무엇(설문)이 붙어 있으면 본문은 늘어나지 않는다.** 늘어나면(flex-1·최소 높이) 설문이
    종이 맨 아래로 밀려 상세(본문 바로 아래)와 배치가 달라진다. 혼자일 때만 남는 자리를 채운다.
  */
  const bodyClass = cn('tiptap-content tiptap-serif focus:outline-none', afterBody ? null : 'min-h-[370px] flex-1')
  const editor = useEditor({
    extensions,
    content: value ?? { type: 'doc', content: [{ type: 'paragraph' }] },
    editorProps: {
      attributes: {
        class: bodyClass,
        'aria-label': ariaLabel,
      },
    },
    onUpdate: ({ editor: ed }) => onChange(ed.getJSON()),
  })

  // 설문을 붙이거나 떼면 본문 상자의 클래스를 바꿔 단다(useEditor 옵션은 처음 값만 본다).
  useEffect(() => {
    editor?.setOptions({ editorProps: { attributes: { class: bodyClass, 'aria-label': ariaLabel } } })
  }, [editor, bodyClass, ariaLabel])

  if (!editor) return null

  const pickImage = () => fileInputRef.current?.click()

  const onFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    try {
      const url = await upload.mutateAsync(file)
      editor.chain().focus().setImage({ src: url }).run()
    } catch {
      /* surfaced below via upload.isError */
    }
  }

  const openLink = () => {
    const prev = editor.getAttributes('link').href as string | undefined
    setLinkValue(prev ?? 'https://')
    setLinkOpen(true)
  }

  const applyLink = () => {
    const url = linkValue.trim()
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
    }
    setLinkOpen(false)
  }

  const uploadError =
    upload.error instanceof UploadValidationError
      ? upload.error.message
      : upload.isError
        ? '이미지 업로드에 실패했어요. 잠시 후 다시 시도해 주세요.'
        : null

  return (
    <div className="flex flex-1 flex-col">
      <Toolbar
        editor={editor}
        onImageClick={pickImage}
        onLinkClick={openLink}
        uploading={upload.isPending}
        extra={toolbarExtra}
      />

      {linkOpen && (
        <div className="mb-3 flex flex-wrap gap-1 rounded-lg border border-border bg-surface-muted p-1">
          <input
            type="url"
            value={linkValue}
            placeholder="https://"
            aria-label="링크 주소"
            autoFocus
            onChange={(e) => setLinkValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                applyLink()
              }
              if (e.key === 'Escape') setLinkOpen(false)
            }}
            className="min-h-11 min-w-0 flex-1 rounded-md border border-border-strong bg-surface px-2 text-sm text-text outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
          />
          <Button type="button" variant="secondary" onClick={applyLink}>
            적용
          </Button>
          <Button type="button" variant="secondary" onClick={() => setLinkOpen(false)}>
            취소
          </Button>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        hidden
        onChange={(e) => void onFileSelected(e)}
      />

      {/*
        **읽기 화면과 같은 종이(ArticlePaper) 안에서 쓴다.** 바탕·테두리·여백 48px·명조체·글자 크기까지
        같은 컴포넌트다. 예전엔 편집기만 종이 밖(카드 안쪽 여백 16px)에 있어 폭이 66px 넓었고, 그
        차이가 줄바꿈을 바꿔 상세와 배치가 어긋났다 (e2e/articles-wysiwyg.spec.ts 가 잰다).
      */}
      <ArticlePaper className={cn('flex flex-1 flex-col', paperClassName)} innerClassName="flex flex-1 flex-col">
        <EditorContent editor={editor} className={cn('flex flex-col', afterBody ? null : 'flex-1')} />
        {afterBody}
      </ArticlePaper>

      {uploadError && (
        <p className="mt-1 text-xs text-danger" role="alert">
          {uploadError}
        </p>
      )}
    </div>
  )
}
